import React, { useMemo, useState } from "react";

import { CartesianGrid, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis, ZAxis } from "recharts";

import { COMPARE_METRICS, MetricDef } from "consts";

import { ValidationNumericField, ValidationRow } from "models";

import { AntModal } from "components";

import { getScenarioColor } from "pages/admin/soilSight/utils/compareUtils.ts";

type ScatterDot = { x: number; y: number; label: string; scenario: string };

const ScatterTooltip = ({ active, payload }: { active?: boolean; payload?: { payload?: ScatterDot }[] }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="soil-sight-scatter-tooltip">
      <div className="soil-sight-scatter-tooltip-label">{d.label}</div>
      <div className="soil-sight-scatter-tooltip-line">
        A: <strong>{d.x.toFixed(2)}</strong>
      </div>
      <div className="soil-sight-scatter-tooltip-line">
        B: <strong>{d.y.toFixed(2)}</strong>
      </div>
      <div style={{ color: d.y > d.x ? "#16a34a" : d.y < d.x ? "#dc2626" : "#6b7280", fontWeight: 600, marginTop: 2 }}>
        {d.y > d.x ? `B higher by ${(d.y - d.x).toFixed(2)}` : d.y < d.x ? `A higher by ${(d.x - d.y).toFixed(2)}` : "Equal"}
      </div>
    </div>
  );
};

export const MultipleVersionScatterGrid = ({
  csvA,
  csvB,
  metrics = COMPARE_METRICS,
}: {
  csvA: ValidationRow[];
  csvB: ValidationRow[];
  metrics?: MetricDef[];
}) => {
  const scenarios = useMemo(() => [...new Set(csvB.map((r) => r.scenario))].sort(), [csvB]);
  const [zoomed, setZoomed] = useState<MetricDef | null>(null);

  const indexA = useMemo(() => {
    const map = new Map<string, ValidationRow[]>();
    for (const row of csvA) {
      const groupKey = `${row.location}|${row.rotation}|${row.scenario}`;
      if (!map.has(groupKey)) map.set(groupKey, []);
      map.get(groupKey)!.push(row);
    }
    return map;
  }, [csvA]);

  const dotsByMetric = useMemo(() => {
    const groupsB = new Map<string, ValidationRow[]>();
    for (const row of csvB) {
      const groupKey = `${row.location}|${row.rotation}|${row.scenario}`;
      if (!groupsB.has(groupKey)) groupsB.set(groupKey, []);
      groupsB.get(groupKey)!.push(row);
    }

    const result: Record<string, Record<string, ScatterDot[]>> = {};
    for (const metric of metrics) {
      result[metric.key] = {};
      for (const sc of scenarios) result[metric.key]![sc] = [];
    }

    for (const [groupKey, bRows] of groupsB) {
      const aRows = indexA.get(groupKey) ?? [];
      // eslint-disable-next-line no-continue
      if (!aRows.length) continue;
      const [loc, rot, sc] = groupKey.split("|") as [string, string, string];

      const avgA = (key: ValidationNumericField) => {
        const vals = aRows.map((r) => r[key]).filter((value): value is number => typeof value === "number");
        return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
      };
      const avgB = (key: ValidationNumericField) => {
        const vals = bRows.map((r) => r[key]).filter((value): value is number => typeof value === "number");
        return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
      };

      for (const metric of metrics) {
        const x = avgA(metric.key);
        const y = avgB(metric.key);
        if (x !== null && y !== null) {
          result[metric.key]![sc]!.push({ x, y, label: `${loc} / ${rot} / ${sc}`, scenario: sc });
        }
      }
    }
    return result;
  }, [csvB, scenarios, indexA, metrics]);

  const renderScatterChart = (metric: MetricDef, height: number, fontSize: number) => {
    const allDots = scenarios.flatMap((sc) => dotsByMetric[metric.key]?.[sc] ?? []);
    const allVals = allDots.flatMap((dot) => [dot.x, dot.y]);
    const minVal = Math.min(...allVals) * 0.98;
    const maxVal = Math.max(...allVals) * 1.02;
    const diagData = [
      { x: minVal, y: minVal },
      { x: maxVal, y: maxVal },
    ];

    return (
      <ResponsiveContainer width="100%" height={height}>
        <ScatterChart margin={{ top: 4, right: 16, bottom: 16, left: -8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis
            type="number"
            dataKey="x"
            domain={[minVal, maxVal]}
            tick={{ fontSize }}
            tickFormatter={(v: number) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : v.toFixed(2))}
            label={{ value: "A", position: "insideBottomRight", offset: -4, fontSize }}
          />
          <YAxis
            type="number"
            dataKey="y"
            domain={[minVal, maxVal]}
            tick={{ fontSize }}
            tickFormatter={(v: number) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : v.toFixed(2))}
            label={{ value: "B", angle: -90, position: "insideLeft", fontSize }}
          />
          <ZAxis range={[30, 30]} />
          <Tooltip content={<ScatterTooltip />} />
          {/* diagonal reference line rendered as a transparent scatter */}
          <Scatter
            data={diagData}
            line={{ stroke: "#9ca3af", strokeDasharray: "4 2" }}
            lineType="fitting"
            shape={() => null as unknown as React.ReactElement}
            fill="none"
          />
          {scenarios.map((sc, i) => (
            <Scatter key={sc} data={dotsByMetric[metric.key]?.[sc] ?? []} fill={getScenarioColor(sc, i)} fillOpacity={0.75} name={sc} />
          ))}
        </ScatterChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div>
      <div className="soil-sight-scatter-hint">
        Each dot = one location/rotation/scenario combination (avg over matched years). Points on the diagonal = no change. Above = B higher, below = A higher.
      </div>
      <div className="soil-sight-scatter-legend">
        {scenarios.map((sc, i) => (
          <span key={sc} className="soil-sight-scatter-legend-item">
            <span className="soil-sight-scatter-legend-dot" style={{ background: getScenarioColor(sc, i) }} />
            {sc}
          </span>
        ))}
      </div>
      <div className="soil-sight-scatter-grid">
        {metrics.map((metric) => (
          <div
            key={metric.key}
            onClick={() => setZoomed(metric)}
            className="soil-sight-scatter-card"
            onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.10)")}
            onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
          >
            <div className="soil-sight-scatter-card-header">
              {metric.label}
              <span className="soil-sight-chart-unit-badge">{metric.unit}</span>
            </div>
            {renderScatterChart(metric, 200, 9)}
          </div>
        ))}
      </div>

      <AntModal
        open={!!zoomed}
        onCancel={() => setZoomed(null)}
        afterOpenChange={(open) => {
          if (open) window.dispatchEvent(new Event("resize"));
        }}
        footer={null}
        width="calc(100vw - 48px)"
        title={zoomed ? `${zoomed.label} (${zoomed.unit}) — A vs B scatter` : null}
        styles={{ body: { paddingTop: 8, height: "calc(100vh - 160px)", overflow: "hidden" } }}
      >
        {zoomed && renderScatterChart(zoomed, Math.floor(window.innerHeight) - 160, 13)}
      </AntModal>
    </div>
  );
};
